import { useMemo } from 'react'
import { Button, JournalEntryStatusPill } from '@cottage/design-system/internal-tooling/ui'
import SearchableSelect, { type SelectOption } from '@/components/SearchableSelect'
import { HoverHelp } from '@/tableControls'
import type { CaseDetail, CaseListItem, ModelSummaryResponse } from '@/services/projections'
import { BARE_TRIGGER, RED_STATUS_TAG } from './gridClasses'
import ScenarioSaveSplitButton from './ScenarioSaveSplitButton'
import CaseJsonMenu from './CaseJsonMenu'
import { todayIsoDate } from '@/utils/waterfallCalculator'
import { expirationDueSoon, formatDate, formatMonthLabel } from './projectionsFormat'
import type { CaseInputs, History, SchemaEntry } from './engine/types'

type ModelBarProps = {
  cases: CaseListItem[]
  selectedCaseId: number | null
  selectedExpiresOn: string | null | undefined
  caseExpired: boolean
  dirty: boolean
  pageCanEdit: boolean
  caseEditable: boolean
  needsRollForward: boolean
  saving: boolean
  importing: boolean
  importedAt: string | null | undefined
  lastClosedMonth: string | null | undefined
  exportEnabled: boolean
  exportDisabledTitle?: string
  schemaStale?: boolean
  exporting: boolean
  onSelectCase: (id: number) => void
  onNew: () => void
  onEditCase: () => void
  onDuplicate: () => void
  onDelete: () => void
  onSave: () => void
  onDiscard: () => void
  onImport: () => void
  onSummary: () => void
  onExport: () => void
  onUploadJson: () => void
  recipeLineCount: number
  holdEnabled: boolean
  onReanchorRecipes: () => void
  onHoldProjectedValues: () => void
  schema: SchemaEntry[]
  history: History | null
  model: ModelSummaryResponse | null
  selectedCase: CaseDetail | null
  saved: CaseInputs
  notes: Record<string, string>
}

export default function ModelBar({
  cases,
  selectedCaseId,
  selectedExpiresOn,
  caseExpired,
  dirty,
  pageCanEdit,
  caseEditable,
  needsRollForward,
  saving,
  importing,
  importedAt,
  lastClosedMonth,
  exportEnabled,
  exportDisabledTitle,
  schemaStale = false,
  exporting,
  onSelectCase,
  onNew,
  onEditCase,
  onDuplicate,
  onDelete,
  onSave,
  onDiscard,
  onImport,
  onSummary,
  onExport,
  onUploadJson,
  recipeLineCount,
  holdEnabled,
  onReanchorRecipes,
  onHoldProjectedValues,
  schema,
  history,
  model,
  selectedCase,
  saved,
  notes,
}: ModelBarProps) {
  const closedMonthLabel = lastClosedMonth
    ? formatMonthLabel(lastClosedMonth.slice(0, 7))
    : null
  const schemaStaleSaveTitle = 'Run Update Historicals first (sheet layout changed)'
  const saveEnabled =
    caseEditable &&
    (dirty || selectedCase?.outputs_stale === true) &&
    !schemaStale &&
    !needsRollForward
  const saveDisabledTitle = schemaStale ? schemaStaleSaveTitle : undefined
  const saveLabel = 'Save'
  const caseActionsDisabled = !pageCanEdit || selectedCaseId == null || schemaStale
  const expiryDueSoon =
    selectedExpiresOn != null && expirationDueSoon(selectedExpiresOn, todayIsoDate())

  const selectedListCase = cases.find((caseItem) => caseItem.id === selectedCaseId)
  const savedAt = selectedCase?.saved_at ?? selectedListCase?.saved_at ?? null
  const summaryEnabled =
    selectedCaseId != null &&
    savedAt != null &&
    !dirty &&
    !caseExpired &&
    selectedCase?.outputs_stale !== true
  const summaryDisabledTitle =
    selectedCaseId == null
      ? 'Select a case to summarise'
      : caseExpired
        ? 'Expired cases cannot be summarised'
        : selectedCase?.outputs_stale === true
          ? 'Save the case first (rolled forward to new actuals)'
          : dirty || savedAt == null
            ? 'Save the case to generate a summary'
            : undefined

  const importStatusLine =
    importedAt && closedMonthLabel
      ? `Actuals through ${closedMonthLabel} · imported ${formatDate(importedAt)}`
      : null

  const caseOptions = useMemo<SelectOption[]>(
    () =>
      cases.map((caseItem) => ({
        id: String(caseItem.id),
        label: caseItem.name,
        caption: caseItem.expires_on
          ? `Expires ${formatDate(caseItem.expires_on)}${caseItem.expired ? ' (expired)' : ''}`
          : caseItem.expired
            ? 'Expired'
            : undefined,
        disabled: false,
      })),
    [cases],
  )

  const expiryPill =
    selectedExpiresOn != null ? (
      caseExpired ? (
        <span className={RED_STATUS_TAG}>Expired</span>
      ) : expiryDueSoon ? (
        pageCanEdit ? (
          <button type="button" className={RED_STATUS_TAG} onClick={onEditCase}>
            Expires {formatDate(selectedExpiresOn)}
          </button>
        ) : (
          <span className={RED_STATUS_TAG}>Expires {formatDate(selectedExpiresOn)}</span>
        )
      ) : pageCanEdit ? (
        <button type="button" onClick={onEditCase}>
          <JournalEntryStatusPill tone="gray">Expires {formatDate(selectedExpiresOn)}</JournalEntryStatusPill>
        </button>
      ) : (
        <JournalEntryStatusPill tone="gray">Expires {formatDate(selectedExpiresOn)}</JournalEntryStatusPill>
      )
    ) : null

  return (
    <div className="flex shrink-0 flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <div className="flex min-w-0 items-center gap-1.5">
            <SearchableSelect
              options={caseOptions}
              value={selectedCaseId != null ? String(selectedCaseId) : null}
              disabled={cases.length === 0}
              placeholder="Select scenario"
              preserveOrder
              className="w-[350px] shrink-0"
              onChange={(id) => {
                const nextId = Number(id)
                if (nextId) onSelectCase(nextId)
              }}
            />
          </div>
          <ScenarioSaveSplitButton
            saveLabel={saveLabel}
            saveEnabled={saveEnabled}
            saving={saving}
            saveHelpText={saveDisabledTitle ?? 'Save scenario changes'}
            caseActionsDisabled={caseActionsDisabled}
            discardEnabled={dirty && caseEditable}
            caseEditable={caseEditable}
            caseExpired={caseExpired}
            recipeLineCount={recipeLineCount}
            holdEnabled={holdEnabled}
            summaryEnabled={summaryEnabled}
            summaryDisabledTitle={summaryDisabledTitle}
            exportEnabled={exportEnabled}
            exportDisabledTitle={exportDisabledTitle}
            exporting={exporting}
            onSave={onSave}
            onEditCase={onEditCase}
            onDuplicate={onDuplicate}
            onDiscard={onDiscard}
            onDelete={onDelete}
            onSummary={onSummary}
            onExport={onExport}
            onReanchorRecipes={onReanchorRecipes}
            onHoldProjectedValues={onHoldProjectedValues}
          />
          <CaseJsonMenu
            schema={schema}
            history={history}
            model={model}
            selectedCase={selectedCase}
            saved={saved}
            notes={notes}
            dirty={dirty}
            pageCanEdit={pageCanEdit}
            onNew={onNew}
            onUploadJson={onUploadJson}
          />
          {expiryPill}
          {dirty ? (
            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
              Unsaved changes
            </span>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {importStatusLine ? (
            <span className="text-sm text-gray-600">{importStatusLine}</span>
          ) : null}
          <span className={BARE_TRIGGER}>
            <HoverHelp
              aria-label="Import help"
              label={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onImport}
                  disabled={!pageCanEdit || importing || (dirty && !schemaStale)}
                >
                  Update Historicals
                </Button>
              }
            >
              {dirty && !schemaStale
                ? 'Save or discard changes before updating historicals'
                : 'Update historical actuals and schema from the CGM Google Sheet'}
            </HoverHelp>
          </span>
        </div>
      </div>
    </div>
  )
}
